import { useMemo } from 'react';
import { css } from '@emotion/react';
import { isEmpty } from 'lodash-es';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Heading, SpinningLoader, Text } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { AppointmentLeadTimeForm } from './AppointmentLeadTimeForm';

export const BookingSiteAppointmentLeadTimeSetting = () => {
  const { t } = useTranslation('schedule-settings');

  const { getScopeName } = useAppScopeStore();

  const { getCFLocationMap } = useSchedulingLocationInfo();

  const onlineSchedulingCFLocationMap = getCFLocationMap('onlinescheduling');

  const onlineSchedulingEnabledLocations = useMemo(() => {
    const onlineSchedulingCFEnabledLocationIds =
      Object.keys(onlineSchedulingCFLocationMap).filter((locationId) => !!onlineSchedulingCFLocationMap[locationId]) ||
      [];
    return onlineSchedulingCFEnabledLocationIds;
  }, [onlineSchedulingCFLocationMap]);

  const listLocationSettingsQuery = SchedulerV3.Queries.useListLocationSettingsQuery({
    request: {
      locationIds: onlineSchedulingEnabledLocations,
    },
    options: {
      enabled: onlineSchedulingEnabledLocations.length > 0,
    },
  });

  const locationsSettings = listLocationSettingsQuery.data?.locationsSettings;
  return (
    <>
      <Heading level={3}>{t('Advance Booking Requirements')}</Heading>
      <Text css={{ marginBottom: theme.spacing(3) }}>
        {t(
          'Set the minimum amount of time required between when a patient books and their appointment time. This helps prevent last-minute bookings and gives you adequate preparation time.'
        )}
      </Text>
      {!listLocationSettingsQuery.isError ? (
        onlineSchedulingEnabledLocations.length === 1 ? (
          listLocationSettingsQuery.isLoading ? (
            <SpinningLoader size='small' />
          ) : (
            <AppointmentLeadTimeForm
              locationId={onlineSchedulingEnabledLocations[0]}
              leadDuration={
                isEmpty(locationsSettings) || isEmpty(locationsSettings?.[onlineSchedulingEnabledLocations[0]])
                  ? 0
                  : locationsSettings?.[onlineSchedulingEnabledLocations[0]]?.leadDuration ?? 0
              }
              css={{ maxWidth: 360 }}
            />
          )
        ) : (
          onlineSchedulingEnabledLocations.map((locationId) => (
            <section key={locationId} css={multiContainerStyle}>
              <Text size='large' css={multiHeadingStyle}>
                <Icon name='location' size={20} css={{ minWidth: 20, minHeight: 20 }} />
                {getScopeName(locationId)}
              </Text>
              {listLocationSettingsQuery.isLoading ? (
                <SpinningLoader size='small' />
              ) : (
                <AppointmentLeadTimeForm
                  locationId={locationId}
                  leadDuration={
                    isEmpty(locationsSettings) || isEmpty(locationsSettings?.[locationId])
                      ? 0
                      : locationsSettings?.[locationId].leadDuration ?? 0
                  }
                />
              )}
            </section>
          ))
        )
      ) : (
        <Text color='error'>{t('Error fetching advance booking requirement setting. Please try again later.')}</Text>
      )}
    </>
  );
};

const multiContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  marginBottom: theme.spacing(2),
  gap: theme.spacing(1),
  [`@media (max-width: ${breakpoints.small.min}px)`]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),

    '& > *:first-of-type': {
      marginBottom: theme.spacing(1),
    },
  },
});

const multiHeadingStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontWeight: 'normal',
  maxWidth: 300,
});
